import * as Sentry from '@sentry/nextjs';
import { useEffect } from 'react';
import { useStorage } from 'reactfire';
import { createWrappedError } from '@/utils/errors';
import { useToast } from '@/hooks/useToast';
import useLoadWorkbookFile from '@/hooks/useLoadWorkbookFile';
import useLoadWorkbookMetaFile from '@/hooks/useLoadWorkbookMetaFile';
import { Loader } from '@/components/loaders/Modal';
import Button from '@/components/Button';
import ModalHoc from '@/components/ModalHoc';
import styles from './styles.module.scss';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
const PREFIX = 'common: components: Editor: prompts: WorkbookSelectConfirmationModal:';

const WorkbookSelectConfirmationModal = _ref => {
  let {
    widgetTitle,
    workbook,
    isLoading = false,
    onClose,
    onConfirm
  } = _ref;
  const storage = useStorage();
  const {
    addToast: sendNotification
  } = useToast();
  const {
    status: workbookDataStatus,
    data: workbookData,
    error: workbookDataError
  } = useLoadWorkbookFile(storage, null, workbook);
  const {
    status: workbookMetaStatus,
    data: workbookMeta,
    error: workbookMetaError
  } = useLoadWorkbookMetaFile(storage, 'success', workbook); // Send error notification for failed workbook load

  useEffect(() => {
    if (workbookDataError) {
      sendNotification({
        description: 'Your workbook failed to load, please reload the page',
        type: 'error'
      });
      Sentry.captureException(Error(`${PREFIX}: workbook load error: ${workbookDataError}`));
    }
  }, [workbookDataError, sendNotification]); // Send warning notifications for assets that
  // might inconvenience user experience

  useEffect(() => {
    if (workbookMetaError) {
      sendNotification({
        description: 'Some assets failed to load and you may experience issues, if so please try reloading the page',
        type: 'warning'
      });
      const wrapperErr = createWrappedError(`${PREFIX} workbook meta loading error`, workbookMetaError);
      Sentry.captureException(wrapperErr);
    }
  }, [workbookMetaError, sendNotification]);
  const isLoaded = workbookDataStatus === 'success' && workbookMetaStatus === 'success';
  return /*#__PURE__*/_jsx("div", {
    className: styles.wrapper,
    children: isLoading ? /*#__PURE__*/_jsx(Loader, {
      className: styles.loader,
      title: ""
    }) : /*#__PURE__*/_jsxs(_Fragment, {
      children: [/*#__PURE__*/_jsx("button", {
        type: "button",
        className: styles.close,
        onClick: () => onClose(),
        children: /*#__PURE__*/_jsx("img", {
          src: "/icons/close.svg"
        })
      }), /*#__PURE__*/_jsx("h4", {
        className: styles.heading,
        children: "Connect Workbook"
      }), /*#__PURE__*/_jsx("div", {
        className: styles.message,
        children: `You're about to connect "${workbook.name || 'Unnamed'}" to "${widgetTitle}". This will replace any updates you've made to this widget's data`
      }), /*#__PURE__*/_jsxs("div", {
        className: styles.actions,
        children: [/*#__PURE__*/_jsx(Button, {
          type: "button",
          variant: 'gray',
          onClick: () => onClose(),
          children: "Cancel"
        }), /*#__PURE__*/_jsx(Button, {
          type: "button",
          variant: "primary",
          onClick: () => onConfirm({
            workbook,
            workbookData,
            workbookMeta
          }),
          disabled: !isLoaded || isLoading,
          children: isLoaded ? 'Confirm' : 'Preparing...'
        })]
      })]
    })
  });
};

export default ModalHoc(WorkbookSelectConfirmationModal, {
  modalClass: styles.modal,
  overlayclass: styles.overlay,
  hasCloseButton: false
});